import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, Avatar, Chip, IconButton,
    Modal, ModalDialog, ModalClose, CircularProgress,
} from '@mui/joy';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import { AppContext } from '../../context';
import {
    APIWorkstationPhotographersStatus,
    APIWorkstationClaimPhotographer,
    APIWorkstationReleasePhotographer,
    parseApiError,
} from '../../api';

const getInitials = (name = '') =>
    name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase();

// shiftId    — id активной смены (к ней прикрепляем/от неё открепляем)
// myAdminId  — id админа этой смены (чтобы отличать «У меня» от «На админе …»)
export default function PointPhotographersPanel({ shiftId, myAdminId }) {
    const { token, showToast } = useContext(AppContext);

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [list, setList] = useState([]);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        const res = await APIWorkstationPhotographersStatus(token);
        setList(Array.isArray(res?.photographers) ? res.photographers : []);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (open) load(); }, [open, load]);

    // Статус относительно текущей смены
    const statusOf = useCallback((p) => {
        if (p.status !== 'busy') return 'free';
        return p.current_admin_id === myAdminId ? 'mine' : 'other';
    }, [myAdminId]);

    const mineCount = useMemo(
        () => list.filter(p => statusOf(p) === 'mine').length,
        [list, statusOf],
    );

    // Прикрепить/забрать себе → один claim (сервер сам открепит у другого админа)
    const handleAttach = async (p, isTake) => {
        if (isTake && !window.confirm(
            `Забрать фотографа ${p.photographer_name} у админа ${p.current_admin_name || '—'} к себе?`
        )) return;
        setBusyId(p.photographer_id);
        const res = await APIWorkstationClaimPhotographer(token, shiftId, p.photographer_id);
        setBusyId(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось прикрепить'), 'danger');
            return;
        }
        showToast(isTake ? `${p.photographer_name} перекреплён к вам` : `${p.photographer_name} прикреплён`, 'success');
        load();
    };

    const handleDetach = async (p) => {
        if (!window.confirm(`Точно открепить фотографа ${p.photographer_name}?`)) return;
        setBusyId(p.photographer_id);
        const res = await APIWorkstationReleasePhotographer(token, shiftId, p.photographer_id);
        setBusyId(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось открепить'), 'danger');
            return;
        }
        showToast(`${p.photographer_name} откреплён`, 'warning');
        load();
    };

    const statusChip = (st, p) => {
        if (st === 'mine') return <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: 10, fontWeight: 700 }}>У меня</Chip>;
        if (st === 'other') return <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: 10, fontWeight: 700 }}>На админе {p.current_admin_name || '—'}</Chip>;
        return <Chip size="sm" variant="soft" color="success" sx={{ fontSize: 10, fontWeight: 700 }}>Свободен</Chip>;
    };

    const actionBtn = (st, p) => {
        if (st === 'mine') return (
            <Button size="sm" variant="soft" color="danger"
                loading={busyId === p.photographer_id}
                onClick={() => handleDetach(p)}
                startDecorator={<RemoveRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ fontWeight: 700 }}>
                Открепить
            </Button>
        );
        if (st === 'other') return (
            <Button size="sm" variant="soft" color="primary"
                loading={busyId === p.photographer_id}
                onClick={() => handleAttach(p, true)}
                startDecorator={<SwapHorizRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ fontWeight: 700 }}>
                Забрать себе
            </Button>
        );
        return (
            <Button size="sm" variant="soft" color="success"
                loading={busyId === p.photographer_id}
                onClick={() => handleAttach(p, false)}
                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ fontWeight: 700 }}>
                Прикрепить
            </Button>
        );
    };

    return (
        <>
            {/* Плашка-триггер */}
            <Box
                onClick={() => setOpen(true)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2.2, py: 2,
                    borderRadius: 'var(--joy-radius-lg)',
                    border: '1px solid var(--joy-palette-divider)',
                    bgcolor: 'rgba(162,38,226,0.08)',
                    cursor: 'pointer', transition: 'all .15s',
                    '&:hover': { borderColor: 'var(--joy-palette-primary-400)', transform: 'translateY(-1px)' },
                }}
            >
                <PeopleAltRoundedIcon sx={{ fontSize: 22, color: 'var(--joy-palette-primary-400)' }} />
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                    Фотографы на смене
                </Typography>
                <Chip size="sm" variant="soft" color="primary" sx={{ fontWeight: 700, fontSize: 11 }}>
                    {mineCount}
                </Chip>
                <ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
            </Box>

            {/* Модалка */}
            <Modal open={open} onClose={() => setOpen(false)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    maxWidth: 460, width: '100%',
                    p: { xs: 2, sm: 2.5 },
                    maxHeight: '92vh', overflowY: 'auto',
                }}>
                    <ModalClose />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pr: 4 }}>
                        <Typography level="h4" sx={{ fontWeight: 800, flex: 1 }}>
                            Фотографы на смене
                        </Typography>
                        <IconButton size="sm" variant="soft" onClick={load} title="Обновить">
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                        Свободных можно прикрепить, «своих» — открепить, а фотографа другого админа —
                        забрать себе.
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : list.length === 0 ? (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', py: 3, textAlign: 'center' }}>
                            Сегодня на точке нет назначенных фотографов
                        </Typography>
                    ) : (
                        <Stack spacing={0.6}>
                            {list.map(p => {
                                const st = statusOf(p);
                                return (
                                    <Box key={p.photographer_id} sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.2,
                                        px: 1.2, py: 0.9,
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        borderRadius: 'var(--joy-radius-md)',
                                    }}>
                                        <Avatar src={p.photographer_avatar_url} size="sm" sx={{
                                            width: 34, height: 34, fontSize: 12, fontWeight: 700,
                                            bgcolor: 'var(--joy-palette-background-level3)',
                                            color: 'var(--joy-palette-text-tertiary)',
                                            border: '1.5px solid var(--joy-palette-divider)', flexShrink: 0,
                                        }}>
                                            {getInitials(p.photographer_name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{
                                                fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)',
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            }}>
                                                {p.photographer_name}
                                            </Typography>
                                            <Box sx={{ mt: 0.3 }}>{statusChip(st, p)}</Box>
                                        </Box>
                                        {actionBtn(st, p)}
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </ModalDialog>
            </Modal>
        </>
    );
}